// src/components/Settings/SettingsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../Layout/Header';
import Sidebar from '../Layout/Sidebar';

const SettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null);
  
  // Settings state
  const [settings, setSettings] = useState({
    // Display settings
    darkMode: false,
    defaultRoute: {
      origin: 'LAX',
      destination: 'TYO'
    },
    defaultCabinClass: 'economy',
    defaultDateRange: '6months',
    
    // Notification settings
    enableNotifications: false,
    notifyOnPriceDrops: true,
    priceDropThreshold: 20,
    
    // Data refresh settings
    autoRefreshDashboard: true,
    refreshInterval: 30, // minutes
  });
  
  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }
    
    // Verify token
    fetch('/api/auth/verify', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(res => res.json())
    .then(data => {
      if (!data.authenticated) {
        localStorage.removeItem('authToken');
        navigate('/login');
      } else {
        // Load settings from localStorage or use defaults
        const savedSettings = localStorage.getItem('userSettings');
        if (savedSettings) {
          setSettings(JSON.parse(savedSettings));
        }
        setLoading(false);
      }
    })
    .catch(err => {
      console.error('Auth verification error:', err);
      navigate('/login');
    });
  }, [navigate]);
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Handle nested settings changes
  const handleNestedChange = (section, name, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [name]: value
      }
    }));
  };
  
  // Handle saving settings
  const handleSave = () => {
    // Save to localStorage
    localStorage.setItem('userSettings', JSON.stringify(settings));
    
    // Show success message
    setSaveStatus('success');
    
    // Clear status after 3 seconds
    setTimeout(() => {
      setSaveStatus(null);
    }, 3000);
  };
  
  // Handle resetting settings to defaults
  const handleReset = () => {
    setSettings({
      darkMode: false,
      defaultRoute: {
        origin: 'LAX',
        destination: 'TYO'
      },
      defaultCabinClass: 'economy',
      defaultDateRange: '6months',
      enableNotifications: false,
      notifyOnPriceDrops: true,
      priceDropThreshold: 20,
      autoRefreshDashboard: true,
      refreshInterval: 30,
    });
    
    // Show success message
    setSaveStatus('reset');
    
    // Clear status after 3 seconds
    setTimeout(() => {
      setSaveStatus(null);
    }, 3000);
  };
  
  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/login');
  };
  
  const cabinClasses = [
    { value: 'economy', label: 'Economy' },
    { value: 'premium', label: 'Premium Economy' },
    { value: 'business', label: 'Business' },
    { value: 'first', label: 'First' },
    { value: 'mint', label: 'Mint (JetBlue Business)' }
  ];
  
  const dateRanges = [
    { value: '1month', label: '1 Month' },
    { value: '3months', label: '3 Months' },
    { value: '6months', label: '6 Months' },
    { value: '1year', label: '1 Year' }
  ];
  
  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onLogout={handleLogout} />
        
        <main className="flex-1 overflow-y-auto p-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h1 className="text-2xl font-bold text-gray-800 mb-6">
                Settings
              </h1>
              
              {/* Save status messages */}
              {saveStatus === 'success' && (
                <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">
                  Settings saved successfully!
                </div>
              )}
              
              {saveStatus === 'reset' && (
                <div className="mb-4 p-3 bg-blue-100 text-blue-700 rounded-lg">
                  Settings reset to defaults.
                </div>
              )}
              
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="text-gray-500 flex items-center">
                    <svg className="animate-spin h-5 w-5 mr-3 text-blue-500" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading settings...
                  </div>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Display Settings Section */}
                  <section>
                    <h2 className="text-lg font-medium text-gray-900 border-b pb-2 mb-4">
                      Display Settings
                    </h2>
                    
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center">
                          <input
                            id="darkMode"
                            name="darkMode"
                            type="checkbox"
                            checked={settings.darkMode}
                            onChange={handleChange}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="darkMode" className="ml-2 block text-sm text-gray-700">
                            Enable Dark Mode
                          </label>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Uses a darker color scheme that's easier on the eyes.
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="defaultOrigin" className="block text-sm font-medium text-gray-700 mb-1">
                            Default Origin
                          </label>
                          <input
                            type="text"
                            id="defaultOrigin"
                            value={settings.defaultRoute.origin}
                            onChange={(e) => handleNestedChange('defaultRoute', 'origin', e.target.value.toUpperCase())}
                            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            maxLength={3}
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="defaultDestination" className="block text-sm font-medium text-gray-700 mb-1">
                            Default Destination
                          </label>
                          <input
                            type="text"
                            id="defaultDestination"
                            value={settings.defaultRoute.destination}
                            onChange={(e) => handleNestedChange('defaultRoute', 'destination', e.target.value.toUpperCase())}
                            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            maxLength={3}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="defaultCabinClass" className="block text-sm font-medium text-gray-700 mb-1">
                          Default Cabin Class
                        </label>
                        <select
                          id="defaultCabinClass"
                          name="defaultCabinClass"
                          value={settings.defaultCabinClass}
                          onChange={handleChange}
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          {cabinClasses.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label htmlFor="defaultDateRange" className="block text-sm font-medium text-gray-700 mb-1">
                          Default Date Range
                        </label>
                        <select
                          id="defaultDateRange"
                          name="defaultDateRange"
                          value={settings.defaultDateRange}
                          onChange={handleChange}
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          {dateRanges.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </section>
                  
                  {/* Notification Settings Section */}
                  <section>
                    <h2 className="text-lg font-medium text-gray-900 border-b pb-2 mb-4">
                      Notification Settings
                    </h2>
                    
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center">
                          <input
                            id="enableNotifications"
                            name="enableNotifications"
                            type="checkbox"
                            checked={settings.enableNotifications}
                            onChange={handleChange}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="enableNotifications" className="ml-2 block text-sm text-gray-700">
                            Enable Browser Notifications
                          </label>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Receive notifications for price drops and other alerts.
                        </p>
                      </div>
                      
                      <div className={settings.enableNotifications ? '' : 'opacity-50'}>
                        <div className="flex items-center">
                          <input
                            id="notifyOnPriceDrops"
                            name="notifyOnPriceDrops"
                            type="checkbox"
                            checked={settings.notifyOnPriceDrops}
                            onChange={handleChange}
                            disabled={!settings.enableNotifications}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="notifyOnPriceDrops" className="ml-2 block text-sm text-gray-700">
                            Notify on Price Drops
                          </label>
                        </div>
                        
                        <div className="mt-2">
                          <label htmlFor="priceDropThreshold" className="block text-sm font-medium text-gray-700 mb-1">
                            Price Drop Threshold (%)
                          </label>
                          <input
                            type="number"
                            id="priceDropThreshold"
                            name="priceDropThreshold"
                            value={settings.priceDropThreshold}
                            onChange={handleChange}
                            disabled={!settings.enableNotifications || !settings.notifyOnPriceDrops}
                            min="1"
                            max="90"
                            className="block w-32 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </section>
                  
                  {/* Data Refresh Settings Section */}
                  <section>
                    <h2 className="text-lg font-medium text-gray-900 border-b pb-2 mb-4">
                      Data Refresh Settings
                    </h2>
                    
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center">
                          <input
                            id="autoRefreshDashboard"
                            name="autoRefreshDashboard"
                            type="checkbox"
                            checked={settings.autoRefreshDashboard}
                            onChange={handleChange}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="autoRefreshDashboard" className="ml-2 block text-sm text-gray-700">
                            Auto-refresh Dashboard
                          </label>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Automatically refresh dashboard data at regular intervals.
                        </p>
                      </div>
                      
                      <div className={settings.autoRefreshDashboard ? '' : 'opacity-50'}>
                        <label htmlFor="refreshInterval" className="block text-sm font-medium text-gray-700 mb-1">
                          Refresh Interval (minutes)
                        </label>
                        <input
                          type="number"
                          id="refreshInterval"
                          name="refreshInterval"
                          value={settings.refreshInterval}
                          onChange={handleChange}
                          disabled={!settings.autoRefreshDashboard}
                          min="5"
                          max="120"
                          className="block w-32 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </section>
                  
                  {/* Action Buttons */}
                  <div className="pt-4 flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={handleReset}
                      className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Reset to Defaults
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Save Settings
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SettingsPage;